import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { environment } from '@et/environment';
import { useBetterAuthForCurrentPlatform } from '@et/guards';

interface BetterAuthGetSessionResponse {
  user?: { twoFactorEnabled?: boolean | null };
  session?: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private msalService = inject(MsalService, { optional: true });
  private oauthService = inject(OAuthService, { optional: true });
  private http = inject(HttpClient);

  /**
   * Ends the active session for whichever auth backend is wired.
   *
   * Better Auth path awaits `POST /api/auth/sign-out` (withCredentials) to drop
   * the issuer session cookie before clearing OAuth tokens, so the next
   * `initCodeFlow` actually re-prompts for credentials instead of silently
   * reusing the existing cookie. Then `oauthService.logOut` redirects through
   * the OIDC end_session endpoint (with `client_id` for Better Auth).
   *
   * `_legacyMsalOptions` is accepted so older native callers that passed MSAL
   * `logout({ onRedirectNavigate })` keep compiling; it is ignored.
   */
  async logout(_legacyMsalOptions?: unknown): Promise<void> {
    if (useBetterAuthForCurrentPlatform() && this.oauthService) {
      try {
        await firstValueFrom(
          this.http.post(
            `${environment.betterAuth.baseUrl}/api/auth/sign-out`,
            {},
            { withCredentials: true },
          ),
        );
      } catch {
        // Still clear local tokens / end_session even if the cookie drop failed.
      }
      // Pass client_id explicitly so the end_session_endpoint accepts the
      // request even when no id_token_hint is available in local storage.
      // Better Auth requires either id_token_hint or client_id when
      // post_logout_redirect_uri is present (RFC 7009 / OIDC Session §5).
      this.oauthService.logOut({ client_id: this.oauthService.clientId });
      return;
    }
    this.msalService?.logoutRedirect();
  }

  /**
   * Reads Better Auth's own `GET /api/auth/get-session` for the currently
   * authenticated user, mainly for `user.twoFactorEnabled` — the live signal of
   * whether this account has actually completed 2FA enrollment (as opposed to
   * `SelfProfile.requiredMfaMethod`, which is Profile v2's policy of whether it
   * *should* be enrolled). Used by `mfaRequiredGuard` to gate mandatory
   * Authenticator-app enrollment. Returns `null` on any transport failure or when
   * there is no active session — callers should treat that as "not enrolled" and
   * let the normal auth guards handle the unauthenticated case.
   */
  getSession(): Observable<{ twoFactorEnabled: boolean } | null> {
    return this.http
      .get<BetterAuthGetSessionResponse | null>(
        `${environment.betterAuth.baseUrl}/api/auth/get-session`,
        { withCredentials: true },
      )
      .pipe(
        map((res) => ({
          twoFactorEnabled: res?.user?.twoFactorEnabled === true,
        })),
        catchError(() => of(null)),
      );
  }
}
